import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Search, Upload, User, LogIn, LogOut, Shield, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useAuth } from '../lib/auth';
import { useUnreadMessages } from '../lib/unreadMessages';
import { useT } from '../i18n';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ComicBubble } from './ComicBubble';

export function TopBar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [q, setQ] = useState('');
    const t = useT();
    const { total: unread } = useUnreadMessages();

    const onSearch = (e) => {
        e.preventDefault();
        if (q.trim()) navigate(`/cerca?q=${encodeURIComponent(q.trim())}`);
    };

    return (
        <header className="alb-glass-bar sticky top-0 z-40">
            <div className="mx-auto max-w-[1400px] px-3 sm:px-5 lg:px-8 h-14 md:h-16 flex items-center gap-3">
                <Link to="/" data-testid="topbar-logo" className="flex items-center gap-2 group">
                    <img src="/logo.jpg" alt="Albambam" className="h-8 w-8 md:h-9 md:w-9 rounded-lg object-cover shadow-[0_0_20px_rgba(244,200,74,0.25)]" />
                    <span className="hidden sm:inline font-display font-bold text-lg md:text-xl alb-text-gradient">Albambam</span>
                </Link>

                <form onSubmit={onSearch} className="ml-2 md:ml-4 flex-1 max-w-xl">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--alb-text-muted)]" />
                        <Input
                            data-testid="topbar-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={t('topbar.search_placeholder')}
                            className="pl-9 h-10 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)] focus-visible:ring-[color:var(--alb-sun)]"
                        />
                    </div>
                </form>

                <div className="flex items-center gap-1 md:gap-2">
                    <LanguageSwitcher compact={!user} />
                    <Link to="/terms" className="hidden sm:inline text-sm text-[color:var(--alb-text-muted)] hover:underline mr-2">{t('legal.terms') || 'Termes'}</Link>
                    <Link to="/privacy" className="hidden sm:inline text-sm text-[color:var(--alb-text-muted)] hover:underline">{t('legal.privacy') || 'Privacitat'}</Link>
                    {user && (
                        <Button data-testid="topbar-upload-button" onClick={() => navigate('/pujar')} className="hidden md:inline-flex bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]">
                            <Upload className="h-4 w-4 mr-1" /> {t('nav.upload')}
                        </Button>
                    )}
                    {user && (
                        <Button data-testid="topbar-friends-button" onClick={() => navigate('/amics')} variant="outline" className="relative hidden lg:inline-flex border-[color:var(--alb-hairline)]">
                            <Users className="h-4 w-4 mr-1" /> {t('nav.friends')}
                            <span className="absolute -top-3 -right-3 pointer-events-none" data-testid="topbar-friends-unread">
                                <ComicBubble count={unread} size="sm" tail="down" />
                            </span>
                        </Button>
                    )}
                    {user && (user.role === 'admin' || user.role === 'employee') && (
                        <Button data-testid="topbar-admin-button" onClick={() => navigate('/admin')} variant="outline" className="hidden lg:inline-flex border-[color:var(--alb-hairline)]">
                            <Shield className="h-4 w-4 mr-1" /> {t('nav.admin')}
                        </Button>
                    )}
                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button data-testid="topbar-profile-menu" variant="ghost" className="p-0 h-10 w-10 rounded-full">
                                    <Avatar className="h-9 w-9 border border-[color:var(--alb-hairline)]">
                                        <AvatarImage src={user.picture} />
                                        <AvatarFallback className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-sun)] font-semibold">{(user.name || 'U').slice(0,1).toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                                <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate('/perfil')}><User className="h-4 w-4 mr-2" /> {t('nav.profile')}</DropdownMenuItem>
                                <DropdownMenuItem data-testid="menu-friends" onClick={() => navigate('/amics')}><Users className="h-4 w-4 mr-2" /> {t('nav.friends')}</DropdownMenuItem>
                                <DropdownMenuItem data-testid="menu-upload" onClick={() => navigate('/pujar')}><Upload className="h-4 w-4 mr-2" /> {t('nav.upload')}</DropdownMenuItem>
                                {(user.role === 'admin' || user.role === 'employee') && (
                                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate('/admin')}><Shield className="h-4 w-4 mr-2" /> {t('nav.admin')}</DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate('/'); }}>
                                    <LogOut className="h-4 w-4 mr-2" /> {t('nav.logout')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <Button data-testid="topbar-login-button" onClick={() => navigate('/entrar', { state: { from: location.pathname } })} variant="outline" className="border-[color:var(--alb-hairline)] hover:bg-[color:var(--alb-surface-2)]">
                            <LogIn className="h-4 w-4 mr-1" /> {t('nav.login')}
                        </Button>
                    )}
                </div>
            </div>
        </header>
    );
}
